import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Card, Divider, Flex, MultiSelect, Stack, Switch, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMemo, useState } from "react";
import { useGetActiveUsersQuery } from "../../graphql/graphql";
import { UserMenuItem } from "../../shared/Misc/UserMenuItem";
export const EmailSettingCard = ({ settingName, isEnabled, toUsers, emailSettingId, onToggle, onUserChange }) => {
    // Controlled search value driven by the MultiSelect's search input.
    // Debounced before being sent to the server so we don't fire a query on
    // every keystroke.
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch] = useDebouncedValue(searchInput, 250);
    // Pause the query entirely while the card is collapsed — the picker UI
    // is only rendered when `isEnabled`, so fetching otherwise would be
    // wasted work and network noise.
    const [{ data }] = useGetActiveUsersQuery({
        variables: { search: debouncedSearch || undefined },
        pause: !isEnabled
    });
    const fetchedUsers = useMemo(() => data?.users?.users ?? [], [data]);
    // Build the option list the MultiSelect renders from. We always include
    // the currently-selected users so their chips show a proper name/avatar,
    // even when a search narrows the fetched results away from them. The
    // `toUsers` payload conveniently already carries name/email/imageUrl,
    // so no extra fetch is needed to resolve selected chips.
    const userSelectData = useMemo(() => {
        const selectedOptions = (toUsers ?? [])
            .filter((u) => Boolean(u?.userId))
            .map((u) => ({
            value: u.userId,
            label: u.name ?? "",
            user: {
                id: u.userId,
                name: u.name ?? "",
                email: u.email ?? "",
                imageUrl: u.imageUrl
            }
        }));
        const fetchedOptions = fetchedUsers.map((user) => ({
            value: user.userId,
            label: user.name,
            user: {
                id: user.userId,
                name: user.name,
                email: user.email,
                imageUrl: user.imageUrl
            }
        }));
        // Merge, deduping by user id. Selected options come first so their
        // label/avatar win when a user happens to be in both lists.
        const seen = new Set();
        const merged = [];
        for (const option of [...selectedOptions, ...fetchedOptions]) {
            if (seen.has(option.value))
                continue;
            seen.add(option.value);
            merged.push(option);
        }
        return merged;
    }, [toUsers, fetchedUsers]);
    const selectedUserIds = useMemo(() => {
        return (toUsers
            ?.map((user) => user?.userId)
            .filter((id) => Boolean(id)) ?? []);
    }, [toUsers]);
    const handleUserSelectionChange = async (userIds) => {
        await onUserChange(emailSettingId, userIds);
    };
    const handleToggle = async () => {
        await onToggle(emailSettingId, !isEnabled);
    };
    return (_jsx(Card, { withBorder: true, children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Flex, { justify: "space-between", align: "flex-start", gap: "sm", children: [_jsxs(Box, { style: { flex: 1 }, children: [_jsx(Text, { variant: "body", size: "md", fw: 600, mb: 1, children: getSettingDisplayName(settingName) }), _jsx(Text, { variant: "meta-muted", lineClamp: 2, children: getSettingDescription(settingName) })] }), _jsx(Switch, { checked: isEnabled, onChange: handleToggle, size: "sm", color: "primary" })] }), isEnabled && (_jsxs(_Fragment, { children: [_jsx(Divider, { my: "xs" }), _jsxs(Stack, { gap: "xs", children: [_jsx(Text, { variant: "body-muted", fw: 500, children: "Notify Users" }), _jsx(MultiSelect, { placeholder: "Select users to notify...", data: userSelectData, value: selectedUserIds, onChange: handleUserSelectionChange, searchable: true, searchValue: searchInput, onSearchChange: setSearchInput, clearable: true, hidePickedOptions: true, maxDropdownHeight: 250, size: "sm", 
                                    // Let the server do the filtering (backend
                                    // searches first/last/full name + email +
                                    // phone). Without this, Mantine would
                                    // additionally filter the already-scoped
                                    // results client-side and hide matches whose
                                    // match only shows up in email/phone fields.
                                    filter: ({ options }) => options, renderOption: ({ option }) => {
                                        const userData = userSelectData.find((u) => u.value === option.value);
                                        return userData?.user ? (_jsx(UserMenuItem, { imageUrl: userData.user.imageUrl, name: userData.user.name, email: userData.user.email })) : null;
                                    } })] })] }))] }) }));
};
const getSettingDisplayName = (settingName) => {
    const settingNames = {
        WEBSITE_LEAD_RECEIVED: "New Website Leads",
        PRIORITY_REFERRAL_RECEIVED: "New Direct Referrals",
        QUEUE_INVITE_RECEIVED: "New Pipeline Entries"
    };
    return settingNames[settingName] || settingName;
};
const getSettingDescription = (settingName) => {
    const descriptions = {
        WEBSITE_LEAD_RECEIVED: "Notify when new website leads are received",
        PRIORITY_REFERRAL_RECEIVED: "Notify when new direct referrals are received",
        QUEUE_INVITE_RECEIVED: "Notify when new pipeline entries are received"
    };
    return descriptions[settingName] || "Email notification setting";
};
